/* ==========================================================================
   Confirmation Page
   Task F18: Shows after successful prescription submission
   ========================================================================== */

import { useParams, useLocation, Link } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { PageContainer } from '@/components/layout/PageContainer'
import { usePortalSlug } from '@/context/PortalContext'
import { CheckCircle, Printer, FileText } from 'lucide-react'

/* --------------------------------------------------------------------------
   Types
   -------------------------------------------------------------------------- */

interface ConfirmationState {
  patientName: string
  productName: string
  submittedAt: string
}

/* --------------------------------------------------------------------------
   Utilities
   -------------------------------------------------------------------------- */

/**
 * Format date/time for display.
 * Example: "January 30, 2025 at 2:34 PM"
 */
function formatDateTime(isoString: string): string {
  const date = new Date(isoString)

  const dateOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }

  const timeOptions: Intl.DateTimeFormatOptions = {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }

  const formattedDate = date.toLocaleDateString('en-US', dateOptions)
  const formattedTime = date.toLocaleTimeString('en-US', timeOptions)

  return `${formattedDate} at ${formattedTime}`
}

/**
 * Trigger browser print dialog.
 */
function handlePrint(): void {
  window.print()
}

/* --------------------------------------------------------------------------
   Summary Row Component
   -------------------------------------------------------------------------- */

interface SummaryRowProps {
  label: string
  value: string
}

function SummaryRow({ label, value }: SummaryRowProps) {
  return (
    <div className="flex justify-between py-3 border-b border-[var(--color-gray-200)] last:border-b-0">
      <span className="text-sm text-[var(--color-gray-600)]">{label}</span>
      <span className="text-sm font-medium text-[var(--color-charcoal)]">
        {value}
      </span>
    </div>
  )
}

/* --------------------------------------------------------------------------
   Component
   -------------------------------------------------------------------------- */

export function Confirmation() {
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const slug = usePortalSlug()

  // Get state passed from PrescriptionForm (if available)
  const state = location.state as ConfirmationState | null

  // Derive pharmacy name from slug (fallback if no portal context)
  // In production, this would come from the portal config
  const pharmacyName = slug
    .split('-')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ')

  return (
    <PageContainer size="sm" className="py-12">
      <div className="text-center">
        {/* Success Icon */}
        <div className="flex justify-center mb-6">
          <div className="w-20 h-20 rounded-full bg-[var(--color-success-light)] flex items-center justify-center">
            <CheckCircle
              className="h-12 w-12 text-[var(--color-success)]"
              strokeWidth={1.5}
            />
          </div>
        </div>

        {/* Title */}
        <h1 className="font-serif text-2xl sm:text-3xl text-[var(--color-charcoal)] mb-2">
          Prescription Submitted
        </h1>

        {/* Subtitle */}
        <p className="text-[var(--color-gray-600)] mb-6">
          Your prescription has been sent for processing
        </p>

        {/* Confirmation Number */}
        {id && (
          <div className="mb-8">
            <p className="text-sm text-[var(--color-gray-500)] mb-1">
              Confirmation Number
            </p>
            <p className="font-mono text-2xl font-semibold text-[var(--pharmacy-primary)]">
              {id}
            </p>
          </div>
        )}

        {/* Summary Card */}
        <Card className="text-left mb-8">
          <div className="flex items-center gap-2 mb-4">
            <FileText className="h-5 w-5 text-[var(--color-gray-500)]" />
            <h2 className="text-sm font-semibold text-[var(--color-charcoal)] uppercase tracking-wide">
              Summary
            </h2>
          </div>

          <div className="divide-y divide-[var(--color-gray-200)]">
            {state?.patientName && (
              <SummaryRow label="Patient" value={state.patientName} />
            )}
            {state?.productName && (
              <SummaryRow label="Product" value={state.productName} />
            )}
            <SummaryRow label="Pharmacy" value={pharmacyName} />
            {state?.submittedAt && (
              <SummaryRow
                label="Submitted"
                value={formatDateTime(state.submittedAt)}
              />
            )}
            {!state && (
              <SummaryRow
                label="Submitted"
                value={formatDateTime(new Date().toISOString())}
              />
            )}
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3 justify-center">
          <Link to={`/rx/${slug}/prescribe`}>
            <Button
              variant="primary"
              size="lg"
              className="w-full sm:w-auto min-w-[200px]"
            >
              Submit Another Prescription
            </Button>
          </Link>
          <Button
            variant="secondary"
            size="lg"
            onClick={handlePrint}
            className="w-full sm:w-auto min-w-[200px]"
          >
            <Printer className="h-4 w-4 mr-2" aria-hidden="true" />
            Print Confirmation
          </Button>
        </div>
      </div>

      {/* Print-only styles */}
      <style>{`
        @media print {
          /* Hide navigation and non-essential elements */
          header, nav, button {
            display: none !important;
          }

          /* Show only the confirmation content */
          body {
            background: white !important;
          }

          /* Make the card full width */
          [class*="PageContainer"] {
            max-width: 100% !important;
            padding: 20px !important;
          }

          /* Hide action buttons */
          a[href^="/"], button {
            display: none !important;
          }

          /* Add print header */
          .print-header {
            display: block !important;
          }
        }

        @media screen {
          .print-header {
            display: none;
          }
        }
      `}</style>
    </PageContainer>
  )
}

export default Confirmation
